// src/components/league/prize/PrizeDistribution.jsx
import React, { useState } from 'react';
import { usePrize, useLeagueData } from '../../../context/DataContext';
import { TrendingUp, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// ── Design tokens ──────────────────────────────────────────────────────────────
const C = {
  bg:        '#000006',
  blue:      '#3f69ae',
  turquoise: '#44d5a3',
  bad:       '#eb5757',
  good:      '#219653',
  grey:      '#8a8a8a',
  surface1:  '#1d1d1d',
  surface2:  '#272727',
  surface3:  '#333333',
  border:    '#4a4a4a',
};

// ── Shared: prize row item ─────────────────────────────────────────────────────
const PrizeRow = ({ rank, label, winner, amount, isPunishment = false }) => {
  const rankColour = rank === 1 ? '#f2c94c' : rank === 2 ? '#c0c0c0' : rank === 3 ? '#cd7f32' : C.turquoise;
  const amountColour = isPunishment ? C.bad : C.turquoise;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '12px 16px',
      backgroundColor: C.surface3,
      borderRadius: '10px',
      border: `1px solid ${C.border}`,
    }}>
      {/* Rank badge */}
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0,
        backgroundColor: `${rankColour}18`,
        border: `1px solid ${rankColour}44`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '13px', fontWeight: 800, color: rankColour,
      }}>
        {rank <= 3 ? ['🥇','🥈','🥉'][rank - 1] : <TrendingUp size={14} style={{ color: rankColour }} />}
      </div>

      {/* Label + winner */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '12px', fontWeight: 700, color: 'white', margin: 0 }}>{label}</p>
        {winner && (
          <p style={{ fontSize: '11px', color: C.grey, margin: '2px 0 0' }}>{winner}</p>
        )}
      </div>

      {/* Amount */}
      <p style={{ fontSize: '18px', fontWeight: 900, color: amountColour, flexShrink: 0, margin: 0 }}>
        {isPunishment ? '-' : ''}${Math.abs(amount)}
      </p>
    </div>
  );
};

// ── Monthly prize card ─────────────────────────────────────────────────────────
const MonthlyPrizeCard = ({ amount, monthlyWinners }) => {
  const months = monthlyWinners
    ? [...new Set(monthlyWinners.map((w) => w.Month))]
    : [];

  const [activeIdx, setActiveIdx] = useState(0);

  const activeMonthWinner = months.length > 0 && monthlyWinners
    ? monthlyWinners.find((w) => w.Month === months[activeIdx])
    : null;

  return (
    <div style={{
      backgroundColor: C.surface2,
      border: `1px solid ${C.border}`,
      borderRadius: '12px',
      padding: '16px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <Calendar size={14} style={{ color: C.blue, flexShrink: 0 }} />
        <p style={{ fontSize: '12px', fontWeight: 700, color: 'white', margin: 0, flex: 1 }}>Monthly Prize</p>
        <p style={{ fontSize: '12px', color: C.turquoise, fontWeight: 700, margin: 0 }}>${amount} / month</p>
      </div>

      {/* Month navigator */}
      {months.length > 0 ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <button
              onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
              disabled={activeIdx === 0}
              style={{
                background: 'none', border: 'none', cursor: activeIdx === 0 ? 'default' : 'pointer',
                color: activeIdx === 0 ? C.border : C.blue, padding: '4px',
              }}
            >
              <ChevronLeft size={18} />
            </button>

            <p style={{ fontSize: '13px', fontWeight: 600, color: 'white', margin: 0 }}>
              {months[activeIdx]}
            </p>

            <button
              onClick={() => setActiveIdx((i) => Math.min(months.length - 1, i + 1))}
              disabled={activeIdx === months.length - 1}
              style={{
                background: 'none', border: 'none', cursor: activeIdx === months.length - 1 ? 'default' : 'pointer',
                color: activeIdx === months.length - 1 ? C.border : C.blue, padding: '4px',
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Winner row */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px',
            backgroundColor: C.surface3,
            borderRadius: '8px',
            border: `1px solid ${C.border}`,
            marginBottom: '12px',
          }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: 'white', margin: 0 }}>
                {activeMonthWinner ? activeMonthWinner.Manager : 'Winner TBD'}
              </p>
              {activeMonthWinner && (
                <p style={{ fontSize: '11px', color: C.grey, margin: '2px 0 0' }}>
                  {Math.round(activeMonthWinner.Total_Net_Points ?? activeMonthWinner.Points)} pts
                </p>
              )}
            </div>
            <p style={{ fontSize: '18px', fontWeight: 900, color: C.turquoise, margin: 0 }}>${amount}</p>
          </div>

          {/* Dot nav */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
            {months.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                style={{
                  width: i === activeIdx ? '14px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  backgroundColor: i === activeIdx ? C.blue : C.border,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  padding: 0,
                }}
              />
            ))}
          </div>
        </>
      ) : (
        <p style={{ fontSize: '12px', color: C.grey, textAlign: 'center', margin: 0 }}>
          Monthly winners available at end of season
        </p>
      )}
    </div>
  );
};

// ── What-if slider ─────────────────────────────────────────────────────────────
const PrizeSlider = ({ value, onChange }) => (
  <div style={{
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '20px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <p style={{ fontSize: '13px', fontWeight: 600, color: C.grey, margin: 0 }}>Hypothetical prize pool</p>
      <p style={{ fontSize: '20px', fontWeight: 900, color: C.turquoise, margin: 0 }}>${value}</p>
    </div>

    <input
      type="range"
      min="100" max="1000" step="50"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{ width: '100%', accentColor: C.turquoise, cursor: 'pointer' }}
    />

    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
      <span style={{ fontSize: '11px', color: C.border }}>$100</span>
      <span style={{ fontSize: '11px', color: C.border }}>$1,000</span>
    </div>
  </div>
);

// ── Achievement badge (bragging rights mode) ───────────────────────────────────
const AchievementBadge = ({ title, winner, rank, description }) => {
  const accent = rank === 1 ? '#f2c94c' : rank === 2 ? '#c0c0c0' : rank === 3 ? '#cd7f32' : C.blue;
  return (
    <div style={{
      backgroundColor: C.surface2,
      border: `1px solid ${accent}44`,
      borderLeft: `3px solid ${accent}`,
      borderRadius: '12px',
      padding: '16px',
    }}>
      <p style={{ fontSize: '10px', fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>
        {rank === 1 ? 'Champion' : rank === 2 ? 'Runner-up' : rank === 3 ? 'Third place' : 'Achievement'}
      </p>
      <p style={{ fontSize: '15px', fontWeight: 800, color: 'white', margin: '0 0 4px' }}>{title}</p>
      {winner && <p style={{ fontSize: '13px', color: C.grey, margin: '0 0 4px' }}>{winner}</p>}
      {description && <p style={{ fontSize: '11px', color: C.grey, margin: 0 }}>{description}</p>}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────
const PrizeDistribution = () => {
  const { getPrizeData } = usePrize();
  const { data } = useLeagueData();
  const prizeData = getPrizeData();

  const [hypotheticalPool, setHypotheticalPool] = useState(prizeData.totalPool || 500);

  const distribution = prizeData.distribution || {
    first: 0.40, second: 0.20, third: 0.10, rankJumper: 0.10, monthly: 0.20,
  };

  const calcPrizes = (pool) => ({
    firstPlace:    Math.round(pool * distribution.first),
    secondPlace:   Math.round(pool * distribution.second),
    thirdPlace:    Math.round(pool * distribution.third),
    rankJumper:    Math.round(pool * distribution.rankJumper),
    monthlyWinners: Math.round(pool * distribution.monthly / 10),
  });

  const prizes = prizeData.mode === 'real'
    ? {
        firstPlace:    prizeData.firstPrize,
        secondPlace:   prizeData.secondPrize,
        thirdPlace:    prizeData.thirdPrize,
        rankJumper:    prizeData.rankJumpPrize,
        monthlyWinners: prizeData.monthlyPrize,
      }
    : calcPrizes(hypotheticalPool);

  // ── Render: What If / real ─────────────────────────────────────────────────
  const renderPrizeContent = () => {
    if (prizeData.mode === 'braggingRights') {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'white', margin: 0 }}>League Achievements</h2>
            <span style={{
              fontSize: '11px', fontWeight: 700, color: C.blue,
              backgroundColor: `${C.blue}18`, border: `1px solid ${C.blue}44`,
              borderRadius: '20px', padding: '3px 10px',
            }}>Bragging Rights</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '12px' }}>
            <AchievementBadge title="League Champion" winner={prizeData.firstPlace}  rank={1} />
            <AchievementBadge title="Runner-up"       winner={prizeData.secondPlace} rank={2} />
            <AchievementBadge title="Third Place"     winner={prizeData.thirdPlace}  rank={3} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <AchievementBadge
              title="Most Improved" winner={prizeData.rankJumpWinner} rank={4}
              description={`GW19 rank ${prizeData.rankJumperGW19Rank} → final rank ${prizeData.rankJumperFinalRank}`}
            />
            <AchievementBadge title="Monthly Stars" description="Managers who topped the monthly points charts" rank={4} />
          </div>
        </>
      );
    }

    // whatIf or real
    const isWhatIf = prizeData.mode !== 'real';

    return (
      <>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, color: 'white', margin: 0 }}>
            {isWhatIf ? 'Prize Simulator' : `Prize Pool: $${prizeData.totalPool}`}
          </h2>
          <span style={{
            fontSize: '11px', fontWeight: 700,
            color: isWhatIf ? C.turquoise : C.good,
            backgroundColor: isWhatIf ? `${C.turquoise}18` : `${C.good}18`,
            border: `1px solid ${isWhatIf ? C.turquoise : C.good}44`,
            borderRadius: '20px', padding: '3px 10px',
          }}>
            {isWhatIf ? 'What If' : 'Real Prizes'}
          </span>
        </div>

        {/* Slider — what-if only */}
        {isWhatIf && (
          <PrizeSlider value={hypotheticalPool} onChange={setHypotheticalPool} />
        )}

        {/* Prize rows — top 3 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <PrizeRow rank={1} label="1st Place"  winner={prizeData.firstPlace}  amount={prizes.firstPlace} />
          <PrizeRow rank={2} label="2nd Place"  winner={prizeData.secondPlace} amount={prizes.secondPlace} />
          <PrizeRow rank={3} label="3rd Place"  winner={prizeData.thirdPlace}  amount={prizes.thirdPlace} />
        </div>

        {/* Special prizes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          {/* Rank Jumper */}
          <div style={{
            backgroundColor: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: '12px',
            padding: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <TrendingUp size={14} style={{ color: C.turquoise, flexShrink: 0 }} />
              <p style={{ fontSize: '12px', fontWeight: 700, color: 'white', margin: 0 }}>Rank Jumper</p>
            </div>
            {prizeData.rankJumpWinner && (
              <p style={{ fontSize: '13px', color: C.grey, margin: '0 0 6px' }}>{prizeData.rankJumpWinner}</p>
            )}
            {prizeData.rankJumperGW19Rank && (
              <p style={{ fontSize: '11px', color: C.grey, margin: '0 0 10px' }}>
                GW19 rank {prizeData.rankJumperGW19Rank} → final {prizeData.rankJumperFinalRank}
              </p>
            )}
            {!prizeData.rankJumperGW19Rank && (
              <p style={{ fontSize: '11px', color: C.grey, margin: '0 0 10px' }}>Most improved from mid-season</p>
            )}
            <p style={{ fontSize: '20px', fontWeight: 900, color: C.turquoise, margin: 0 }}>${prizes.rankJumper}</p>
          </div>

          {/* Monthly */}
          <MonthlyPrizeCard
            amount={prizes.monthlyWinners}
            monthlyWinners={data?.monthlyTopPerformers}
          />
        </div>

        {/* What-if footnote */}
        {isWhatIf && (
          <p style={{ fontSize: '11px', color: C.grey, textAlign: 'center', marginTop: '16px', lineHeight: 1.5 }}>
            Adjust the slider to simulate different prize pools. Distribution: 40 / 20 / 10 / 10 / 20% monthly.
          </p>
        )}
      </>
    );
  };

  return (
    <div style={{
      backgroundColor: C.surface1,
      border: `1px solid ${C.border}`,
      borderRadius: '16px',
      padding: '24px',
    }}>
      {renderPrizeContent()}
    </div>
  );
};

export default PrizeDistribution;
